import type { AlertSubscriptionDto } from '../models/alert.dto'
import type { AlertRule, AlertLevelPresentation } from '../models/alert.types'
import type {
  AlertHistoryRow,
  TriggeredAlertDto,
  TriggeredAlertOccurrenceDto,
} from '../models/history.types'
import type { AlertLevelMetadata, AlertMetricCatalog } from '../models/reports-bridge'

import { alertDimensions, type AlertDimensionMapperDependencies } from './alertDimensionMapper'
import {
  alertCondition,
  alertConditionForMetric,
  alertConditionJoin,
  alertConditions,
  alertFilterSubtext,
  alertLevelPresentation,
  alertMetricField,
  alertSubscriptionChannels,
  readCustomWindowDays,
} from './alertMapper'
import { alertScalarList } from './alertScalarList'
import { resolveAlertThreshold } from './alertTrend'

import { ALERT_RELIABLE_CHART_START_DATE } from '@/shared/config'

export interface AlertReadMapperDependencies extends AlertDimensionMapperDependencies {
  catalog: AlertMetricCatalog
  levels: AlertLevelMetadata
}

/** The live triggered-alerts endpoint sends numeric fields inconsistently — confirmed against a
 * real response: `metric_alert_value` as a numeric string ("138.0"), `metric_values` as a
 * single-element array ([138]), `compare_value` as a real number. Old tolerates all three
 * implicitly (loose `||` plus a coercing formatter); this mirrors that tolerance explicitly. */
function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  if (Array.isArray(value)) return finiteNumber(value[0])
  return null
}

function levelName(presentation: AlertLevelPresentation, catalog: AlertMetricCatalog): string {
  if (presentation.source === 'catalog') return presentation.label
  if (presentation.source === 'partition') return presentation.labelParams.value
  return catalog.metricLabel('campaign')
}

function triggerCounts(triggered: readonly TriggeredAlertDto[]): Map<number, number> {
  const counts = new Map<number, number>()
  for (const item of triggered) {
    const count = finiteNumber(item.total_count) ?? 0
    counts.set(item.config_id, (counts.get(item.config_id) ?? 0) + count)
  }
  return counts
}

export function alertRules(
  subscriptions: readonly AlertSubscriptionDto[],
  triggered: readonly TriggeredAlertDto[],
  dependencies: AlertReadMapperDependencies,
  workspaceIds?: readonly (number | string)[],
): AlertRule[] {
  const counts = triggerCounts(triggered)
  return subscriptions.map((subscription) => {
    const details = subscription.content_details
    return {
      id: subscription.id,
      name: details.name,
      levelName: levelName(
        alertLevelPresentation(details, dependencies.levels, workspaceIds),
        dependencies.catalog,
      ),
      isActive: subscription.is_active,
      triggerCount: counts.get(subscription.id) ?? 0,
      createdAt: subscription.created_at ?? null,
      condition: alertCondition(details, dependencies.catalog),
      conditions: alertConditions(details, dependencies.catalog),
      conditionJoin: alertConditionJoin(details),
      filterSubtext: alertFilterSubtext(details),
      channels: alertSubscriptionChannels(subscription.channel_type),
      recipientEmails: alertScalarList(subscription.allowed_emails),
      whatsappNumbers: alertScalarList(subscription.whatsapp_numbers),
      raw: details,
    }
  })
}

function occurrenceHistory(
  occurrence: TriggeredAlertOccurrenceDto,
): Array<Record<string, number | string>> {
  const details = occurrence.details
  if (!details) return []
  const candidate = Object.entries(details).find(
    ([key, value]) =>
      key.startsWith('last_') && key.endsWith('_days_history') && Array.isArray(value),
  )?.[1]
  if (!Array.isArray(candidate)) return []
  return candidate.flatMap((row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return []
    const normalized = Object.fromEntries(
      Object.entries(row).filter(
        ([, value]) =>
          typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value)),
      ),
    ) as Record<string, number | string>
    return Object.keys(normalized).length ? [normalized] : []
  })
}

function occurredAt(
  occurrence: TriggeredAlertOccurrenceDto,
  item: TriggeredAlertDto,
): string | null {
  return occurrence.created_at ?? item.last_triggered_at ?? null
}

function timeValue(value: string | null): number {
  if (!value) return 0
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function historyRow(
  item: TriggeredAlertDto,
  occurrence: TriggeredAlertOccurrenceDto,
  subscription: AlertSubscriptionDto,
  dependencies: AlertReadMapperDependencies,
  workspaceIds?: readonly (number | string)[],
): AlertHistoryRow | null {
  const details = subscription.content_details
  const metricField = occurrence.metric || alertMetricField(details)
  const condition = alertConditionForMetric(details, dependencies.catalog, metricField)
  if (!condition || condition.amount === null || !metricField) return null

  const actual =
    finiteNumber(occurrence.metric_alert_value) ?? finiteNumber(occurrence.details?.metric_values)
  const baseline = finiteNumber(occurrence.details?.compare_value)
  const changePct =
    actual === null || baseline === null || baseline === 0
      ? null
      : ((actual - baseline) * 100) / baseline
  const triggeredAt = occurredAt(occurrence, item)
  const column = dependencies.catalog.getColumn(metricField) ?? {
    field: metricField,
    header: dependencies.catalog.metricLabel(metricField),
    numeric: true,
  }

  return {
    id: subscription.id || item.config_id,
    name: details.name,
    levelName: levelName(
      alertLevelPresentation(details, dependencies.levels, workspaceIds),
      dependencies.catalog,
    ),
    metricField,
    column,
    operator: condition.operator,
    thresholdValue: condition.amount,
    isPercentage: condition.mode === 'percentage',
    resolvedThreshold: resolveAlertThreshold(
      baseline,
      condition.amount,
      condition.mode === 'percentage',
      condition.operator,
    ),
    window: details.alert_window.type,
    customWindowDays: readCustomWindowDays(details),
    compareWindow: condition.compareWindow,
    actual,
    baseline,
    changePct,
    changeTone: changePct === null || changePct === 0 ? 'flat' : changePct > 0 ? 'up' : 'down',
    triggeredAt,
    compareDate:
      typeof occurrence.details?.compare_date === 'string' ? occurrence.details.compare_date : null,
    channels: alertSubscriptionChannels(subscription.channel_type),
    dimensions: alertDimensions(occurrence, dependencies),
    history: occurrenceHistory(occurrence),
    isChartReliable: timeValue(triggeredAt) >= Date.parse(ALERT_RELIABLE_CHART_START_DATE),
  }
}

export function alertHistory(
  subscriptions: readonly AlertSubscriptionDto[],
  triggered: readonly TriggeredAlertDto[],
  dependencies: AlertReadMapperDependencies,
  workspaceIds?: readonly (number | string)[],
): AlertHistoryRow[] {
  const subscriptionsById = new Map(subscriptions.map((item) => [item.id, item]))
  const rows: AlertHistoryRow[] = []
  for (const item of triggered) {
    const subscription = item.subscription ?? subscriptionsById.get(item.config_id)
    if (!subscription) continue
    for (const occurrence of item.occurrences ?? []) {
      const row = historyRow(item, occurrence, subscription, dependencies, workspaceIds)
      if (row) rows.push(row)
    }
  }
  return rows.sort((left, right) => timeValue(right.triggeredAt) - timeValue(left.triggeredAt))
}
